import styles from "./ShapeEditor.scss";
import { Check, X } from "lucide-react";
import React from "react";
import { Aabox, Box, Circle, Contour, Entity } from "app/js/types";
import classNames from "classnames";

type ShapeProperties = {
  aabox: Aabox | null;
  box: Box | null;
  circle: Circle | null;
  contour: Contour | null;
};
export type OnApplyCallback = (
  entityId: number,
  newShape: ShapeProperties,
) => void;

interface ShapeEditorProps {
  entity: Entity;
  onApply: OnApplyCallback;
  onClose: () => void;
}

function stringifyShapes(shapes: ShapeProperties) {
  const fullShapes = {
    aabox: shapes.aabox ?? null,
    box: shapes.box ?? null,
    circle: shapes.circle ?? null,
    contour: shapes.contour ?? null,
  };
  // contour requires something more complicated than just JSON.stringify to look pretty
  let stringifiedShapes = JSON.stringify(fullShapes, null, 2);
  if (!!fullShapes.contour) {
    // I don't make any special checks, because this type of values normally would only appear for contours
    stringifiedShapes = stringifiedShapes.replace(
      /\[[\n\s]+([\d.]+),[\n\s]+([\d.]+)[\n\s]+]/gm,
      "[$1, $2]",
    );
  }
  return stringifiedShapes;
}

export default function ShapeEditor({
  entity,
  onApply,
  onClose,
}: ShapeEditorProps) {
  const defaultShape = stringifyShapes({
    aabox: entity.aabox,
    box: entity.box,
    circle: entity.circle,
    contour: entity.contour,
  });
  const [shapeValue, setShapeValue] = React.useState<string>(defaultShape);
  const [error, setError] = React.useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setError(null);
    setShapeValue(e.target.value);
  };

  const handleApply = () => {
    let value;
    // TODO: add proper validation of object structure
    try {
      value = JSON.parse(shapeValue);
    } catch (e) {
      setError(e.message);
      return;
    }

    onApply(entity.id, value);
  };

  const handleClose = () => {
    onClose();
  };

  const hasError = !!error;

  return (
    <div className={styles.container}>
      <div className={styles.shapeEditor}>
        <textarea
          value={shapeValue}
          onChange={handleChange}
          className={classNames({ [styles.error]: hasError })}
        />
      </div>
      {hasError && (
        <div className={styles.shapeErrors}>
          <span>{error}</span>
        </div>
      )}
      <div className={styles.buttonsBlock}>
        <button title="Apply changes" onClick={handleApply}>
          <Check />
        </button>
        <button title="Discard changes" onClick={handleClose}>
          <X />
        </button>
      </div>
    </div>
  );
}
